import { scrubExceptionEvent, stripQueryAndHash } from '../../../../shared/exception-scrubber';

/** Applies to SDK events too, including automatic navigation and click capture. */
export function prepareBrowserEvent<T extends { event: string; properties: Record<string, unknown> } | null>(event: T): T {
  const scrubbed = scrubExceptionEvent(event);
  if (!scrubbed) return scrubbed;
  const properties = { ...scrubbed.properties, product: 'preview_planner', event_source: 'browser' } as Record<string, unknown>;
  const result = { ...scrubbed, properties };
  const containers: Record<string, unknown>[] = [properties];
  // Identify events carry person updates on the envelope; other events nest them.
  for (const parent of [properties, result as Record<string, unknown>]) {
    for (const key of ['$set', '$set_once']) {
      const value = parent[key];
      if (value && typeof value === 'object') {
        const copy = { ...value }; parent[key] = copy; containers.push(copy);
      }
    }
  }
  for (const container of containers) {
    for (const key of ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer', '$prev_pageview_pathname', '$session_entry_url', '$session_entry_referrer']) {
      if (typeof container[key] === 'string') container[key] = stripQueryAndHash(container[key]);
    }
  }
  // Titles can carry unit names the model wrote. Navigation needs only its path.
  delete properties.$title;
  // The SDK still adds link destinations even with attribute masking enabled.
  // Keep structural selectors for click analysis; never collect signed content URLs.
  if (typeof properties.$elements_chain === 'string') {
    properties.$elements_chain = properties.$elements_chain.replace(/(?:attr__)?href="(?:\\.|[^"\\])*"/g, '');
  }
  if (Array.isArray(properties.$elements)) {
    properties.$elements = properties.$elements.map(element => {
      const clean = { ...element }; delete clean.attr__href; return clean;
    });
  }
  delete properties.$external_click_url;
  // Heatmaps use URLs as object keys, independently of $current_url.
  if (properties.$heatmap_data && typeof properties.$heatmap_data === 'object') {
    const heatmap: Record<string, unknown[]> = {};
    for (const [url, points] of Object.entries(properties.$heatmap_data)) {
      if (Array.isArray(points)) (heatmap[stripQueryAndHash(url)] ??= []).push(...points);
    }
    properties.$heatmap_data = heatmap;
  }
  return result;
}
